import { isThetaError } from "@theta/api-client";

/**
 * WHAT A FAILED WRITE SAYS TO THE PERSON WHO ATTEMPTED IT
 *
 * A 422 carries TWO things: an envelope message and a `fields[]` array of
 * `{path, code, message}`, one per thing that was actually wrong. `unwrap`
 * (packages/api-client) has always put both on `ThetaError`.
 *
 * Nothing read the second one. And for `validationFailed` the envelope message
 * is the literal string "Ugyldige felter." — "some fields are invalid", which
 * names neither the field nor the reason. So a post refused for a duplicate
 * poll option ("To svarmuligheder kan ikke være ens.", `polls.0.options.1`)
 * told the author either that, which is not actionable, or — where the surface
 * showed nothing at all — nothing (OSO-443).
 *
 * So: when there are fields, THE FIELD MESSAGES ARE THE MESSAGE. They are
 * written in Danish server-side and each says what to change; the envelope's
 * "one of these is invalid" adds nothing in front of them.
 *
 * `path` is deliberately NOT shown. It is `polls.0.options.1` — a request-body
 * pointer, useful in a log and noise to a reader who never saw the request.
 *
 * THE FALLBACK IS FOR WHAT NOBODY WROTE FOR A READER. A network failure and an
 * aborted request are not `ThetaError`s at all. A proxy's HTML 502 IS one —
 * `unwrap` makes a `ThetaError` of every non-2xx, and when the body is not the
 * contract's shape it has nothing to put in `message` but the status line, so
 * it synthesises `HTTP 502`. Showing that to a member is showing them the
 * inside of the plumbing: it is the same "we do not know" the fallback is
 * there for, so it takes the fallback.
 *
 * Everything the server itself named, it named in Danish — the schema-level
 * messages included, since `z.config(z.locales.da())` at the backend's config
 * module — and passing that through beats replacing it with a guess.
 */

/** `unwrap`'s stand-in for a body that carried no message of its own. */
const SYNTHESISED_STATUS = /^HTTP \d{3}$/;

export function errorText(err: unknown, fallback: string): string {
  if (!isThetaError(err)) return fallback;
  if (err.fields.length === 0 && SYNTHESISED_STATUS.test(err.message)) {
    return fallback;
  }
  // Deduplicated: five options duplicating the same one produce five identical
  // field messages, and "…kan ikke være ens." five times is not five facts.
  const reasons = [
    ...new Set(err.fields.map((f) => f.message.trim()).filter(Boolean)),
  ];
  return reasons.length > 0 ? reasons.join(" ") : err.message;
}
